import { useEffect, useState, useCallback } from "react";
import { PaginationControl } from "../../components/PaginationControl";
import { useNotifications } from "../../hooks/useNotifications";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { fetchNotifications, markNotificationRead, type Notification } from "../../slices/notificationSlice";
import { useNavigate } from "react-router-dom";
import {
  FilterIcon,
  RefreshCcwIcon,
  Bell,
  BellOff,
  X,
  Search,
  XCircle,
} from "lucide-react";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import api from "../../lib/axios";
import { toast } from "react-toastify";
import { RoleBasedLayout } from "../../components/RoleBasedLayout";
import { createPortal } from "react-dom";
import { NotificationSkeletonList } from "../../components/NotificationSkeleton";
import { iconMap } from "../../components/IconMap";

// Notification types configuration
interface NotificationTypeConfig {
  icon: string;
  bgColor: string;
  borderColor: string;
  iconBgColor: string;
  iconColor: string;
  badgeColor: string;
}

const NOTIFICATION_TYPES: Record<string, NotificationTypeConfig> = {
  "New Employee Registered": {
    icon: "newUser",
    bgColor: "bg-green-50",
    borderColor: "border-green-400",
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    badgeColor: "bg-green-500",
  },
  "New Store Registered": {
    icon: "newStore",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-400",
    iconBgColor: "bg-purple-100",
    iconColor: "text-purple-600",
    badgeColor: "bg-purple-500",
  },
  "Inventory Alert": {
    icon: "alert",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-400",
    iconBgColor: "bg-amber-100",
    iconColor: "text-amber-600",
    badgeColor: "bg-amber-500",
  },
};

// Default config for unknown notification types
const DEFAULT_NOTIFICATION_CONFIG: NotificationTypeConfig = {
  icon: "notification",
  bgColor: "bg-blue-50",
  borderColor: "border-primary",
  iconBgColor: "bg-blue-100",
  iconColor: "text-primary",
  badgeColor: "bg-primary",
};

// Helper function to get notification type config
const getNotificationConfig = (title: string | null): NotificationTypeConfig => {
  if (!title) return DEFAULT_NOTIFICATION_CONFIG;
  return NOTIFICATION_TYPES[title] || DEFAULT_NOTIFICATION_CONFIG;
};


const NotificationsPage = () => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    notifications,
    pagination,
    markNotificationAsRead,
    // markAllAsRead,
  } = useNotifications();

  // Get read notifications from Redux state (updates when marked as read)
  const readIdsFromState = useAppSelector((state) => state.notifications.read);

  // Push notifications
  const {
    isSupported,
    isSubscribed,
    isLoading: pushLoading,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const [sendingTest, setSendingTest] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [initialLoading, setInitialLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const fetchNotificationsHandler = async () => {
    if (initialLoading) {
      setInitialLoading(true);
    }

    try {
      await dispatch(
        fetchNotifications({
          page,
          limit: 10,
          search,
          startDate,
          endDate,
        })
      );
    } finally {
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  };



  const [page, setPage] = useState<number>(1);

  /** Run when filters are applied */
  const handleSearch = useCallback(() => {
    setPage(1);
    dispatch(fetchNotifications({
      page: 1,
      limit: 10,
      search,
      startDate,
      endDate,
    }));
  }, [search, startDate, endDate, dispatch]);

  /** Load data whenever page changes */
  useEffect(() => {
    fetchNotificationsHandler();
  }, [page]);

  /** Debounce search - trigger search after user stops typing */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) {
        handleSearch();
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [search, handleSearch]);

  /** Handle search input Enter key */
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Use Redux state for read notifications (updates reactively)
  const readIds = readIdsFromState.length > 0 ? readIdsFromState : JSON.parse(localStorage.getItem("read_notifications") || "[]");

  /** Handle notification click - open modal and mark as read */
  const handleNotificationClick = (notification: Notification) => {
    setSelectedNotification(notification);
    if (!readIds.includes(notification.id)) {
      markNotificationAsRead(notification.id);
    }
  };

  /** Close notification detail modal */
  const closeNotificationModal = () => {
    setSelectedNotification(null);
  };

  /** Handle marking all currently rendered notifications as read */
  const handleMarkAllAsRead = () => {
    const unreadNotificationIds = notifications
      .filter((notif: Notification) => !readIds.includes(notif.id))
      .map((notif: Notification) => notif.id);

    if (unreadNotificationIds.length > 0) {
      // Mark only the currently visible unread notifications
      dispatch(markNotificationRead(unreadNotificationIds));
    }
  };

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      toast.success("Push notifications enabled!");
    } else {
      toast.error("Failed to enable push notifications");
    }
  };

  const handleUnsubscribe = async () => {
    const success = await unsubscribe();
    if (success) {
      toast.success("Push notifications disabled");
    } else {
      toast.error("Failed to disable push notifications");
    }
  };

  const handleTestNotification = async () => {
    setSendingTest(true);
    try {
      await api.post("/push/test");
      toast.success("Test notification sent! Check your notifications.");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send test notification");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <RoleBasedLayout>
      <div className="p-6 mx-auto">
        {/* Push Notifications Test Panel */}
        <div className="bg-white p-4 rounded-xl shadow mb-5 border border-blue-200">
          <h2 className="text-lg font-bold text-primary mb-3">Push Notifications</h2>

          {!isSupported ? (
            <div className="text-sm text-gray-600">
              <p>Push notifications are not supported in this browser.</p>
              <p className="mt-1 text-xs">Make sure you're using a modern browser like Chrome, Firefox, or Edge.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isSubscribed ? "bg-green-500" : "bg-gray-400"}`}></div>
                <span className="text-sm">
                  Status: {isSubscribed ? "Subscribed" : "Not subscribed"}
                </span>
              </div>

              <div className="flex gap-2 flex-wrap">
                {!isSubscribed ? (
                  <button
                    onClick={handleSubscribe}
                    disabled={pushLoading}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 text-sm"
                  >
                    <Bell size={16} />
                    {pushLoading ? "Subscribing..." : "Enable Push Notifications"}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleUnsubscribe}
                      disabled={pushLoading}
                      className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 text-sm"
                    >
                      <BellOff size={16} />
                      {pushLoading ? "Unsubscribing..." : "Disable Push Notifications"}
                    </button>
                    <button
                      onClick={handleTestNotification}
                      disabled={sendingTest}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 text-sm"
                    >
                      {sendingTest ? "Sending..." : "Send Test Notification"}
                    </button>
                  </>
                )}
              </div>

              {isSubscribed && (
                <p className="text-xs text-gray-500">
                  You'll receive push notifications for inventory alerts, subscription reminders, and other important updates.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Search Bar - Always Visible */}
        <div className="bg-white p-4 rounded-xl shadow mb-5">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search notifications..."
                className="w-full border border-gray-300 rounded-md pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 flex items-center gap-2"
            >
              <Search size={18} />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>
        </div>

        {/* MOBILE FILTER BUTTON */}
        <div className="md:hidden flex justify-end mb-4">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="bg-primary text-white px-4 py-2 rounded-md flex gap-2 items-center"
          >
            Filters
            <FilterIcon className="ms-1" />
          </button>
        </div>

        {/* Filters Section */}
        <div className="hidden md:block bg-white p-4 rounded-xl shadow mb-5">
          <h1 className="font-bold text-lg my-2">Search Filters</h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* <div className="flex flex-col">
                <label htmlFor="" className="form-label">Search</label>
                <input
                    type="text"
                    placeholder="Search notifications..."
                    className="border p-2 rounded"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div> */}

            <div className="flex flex-col">
              <label htmlFor="" className="form-label">Start Date</label>
              <input
                type="date"
                className="border p-2 rounded"
                value={startDate}
                placeholder="Start Date"
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="" className="form-label">End Date</label>
              <input
                type="date"
                className="border p-2 rounded"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={() => setUnreadOnly((prev) => !prev)}
            />
            <label>Show Unread Only</label>

            <button
              onClick={() => {
                dispatch(fetchNotifications({}))
              }}
              className="ms-6 px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200"
            >
              <RefreshCcwIcon />
            </button>
            <button
              onClick={handleSearch}
              className="ml-auto bg-blue-600 text-white px-4 py-2 rounded"
            >
              Apply Filters
            </button>
          </div>

        </div>

        {/* Mobile Filters Modal */}
        {showMobileFilters && createPortal(
          <div className="modal" onClick={() => setShowMobileFilters(false)}>
            <div className="modal-body p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-primary">Filters</h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col">
                  <label className="form-label mb-1">Start Date</label>
                  <input
                    type="date"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="flex flex-col">
                  <label className="form-label mb-1">End Date</label>
                  <input
                    type="date"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="unread-only"
                    checked={unreadOnly}
                    onChange={() => setUnreadOnly(prev => !prev)}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="unread-only" className="text-sm font-medium">
                    Show Unread Only
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setUnreadOnly(false);
                    handleSearch();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    handleSearch();
                    setShowMobileFilters(false);
                  }}
                  className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:opacity-90"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Notification Detail Modal */}
        {selectedNotification && createPortal(
          <div className="modal" onClick={closeNotificationModal}>
            <div
              className="modal-body p-0 max-w-lg w-full mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const config = getNotificationConfig(selectedNotification.title);
                return (
                  <>
                    {/* Header with colored background */}
                    <div className={`${config.bgColor} p-6 border-b ${config.borderColor}`}>
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full ${config.iconBgColor} flex items-center justify-center`}>
                          {iconMap[config.icon]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-xl font-bold text-gray-900">{selectedNotification.title}</h2>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(selectedNotification.created_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={closeNotificationModal}
                          className="flex-shrink-0 text-red-500 hover:text-red-600 transition-colors"
                        >
                          <XCircle size={24} />
                        </button>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-6">
                      {selectedNotification.summary && (
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary</h3>
                          <p className="text-gray-700">{selectedNotification.summary}</p>
                        </div>
                      )}

                      {selectedNotification.description && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Details</h3>
                          <div className="text-gray-700 whitespace-pre-wrap">{selectedNotification.description}</div>
                        </div>
                      )}

                      {!selectedNotification.summary && !selectedNotification.description && (
                        <p className="text-gray-500 italic">No additional details available.</p>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col gap-2">
                      {selectedNotification.title === "Inventory Alert" && (
                        <button
                          onClick={() => {
                            closeNotificationModal();
                            navigate("/manager/items");
                          }}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md transition-opacity font-medium"
                        >
                          View Low Stock Items
                        </button>
                      )}
                      <button
                        onClick={closeNotificationModal}
                        className="w-full bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>,
          document.body
        )}

        {/* Notifications List Header with Mark All as Read */}
        {!initialLoading && notifications.length > 0 && (
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Notifications ({notifications.filter((n: Notification) => !readIds.includes(n.id)).length} unread)
            </h2>
            {notifications.some((n: Notification) => !readIds.includes(n.id)) && (
              <button
                onClick={handleMarkAllAsRead}
                className="bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 text-sm font-medium flex items-center gap-2"
              >
                Mark All as Read
              </button>
            )}
          </div>
        )}

        {/* Notifications List */}
        {initialLoading ? (
          <NotificationSkeletonList count={10} />
        ) : notifications.length === 0 ? (
          <p>No notifications found.</p>
        ) : (
          <div className="space-y-4">
            {notifications.map((notif: Notification, index) => {
              const isUnread = !readIds.includes(notif.id);
              const config = getNotificationConfig(notif.title);

              if (unreadOnly && !isUnread) return null;

              return (
                <div
                  key={index}
                  onClick={() => handleNotificationClick(notif)}
                  className={`border p-4 rounded-xl bg-white shadow cursor-pointer transition-all hover:shadow-md ${isUnread
                      ? `border-2 ${config.borderColor} ${config.bgColor}`
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config.iconBgColor} flex items-center justify-center`}>
                      {iconMap[config.icon]}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h2 className="text-lg text-gray-900 font-semibold truncate">{notif.title}</h2>
                        {isUnread && (
                          <span className={`${config.badgeColor} text-white text-xs px-2 py-1 rounded-full flex-shrink-0`}>
                            New
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">{notif.summary}</p>
                      <p className="text-gray-400 text-xs mt-2">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}


        {/* Pagination */}
        {pagination && (
          <PaginationControl
            page={pagination.page as number}
            totalPages={pagination.totalPages as number}
            onPageChange={(newPage) => setPage(newPage)}
          />
        )}
      </div>
    </RoleBasedLayout>
  );
};

export default NotificationsPage;
